import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaBars, FaTimes, FaTrash, FaSignOutAlt } from "react-icons/fa";
import api, { clearAuthToken } from "./api/axiosConfig";
import Board from "./components/board/Board";
import CollapsibleCreateBoardForm from "./forms/CollapsibleCreateBoardForm";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import LandingPage from "./pages/LandingPage";
import InviteUserForm from "./components/board/InviteUserForm";
import InvitedUsers from "./components/board/InvitedUsers";
import ThemeToggle from "./components/common/ThemeToggle";
import "./styles/index.css";

function Logo() {
  return (
    <svg width="32" height="32" viewBox="0 0 100 100" aria-hidden="true">
      <rect width="100" height="100" fill="#3f912f" rx="22" />
      <rect x="20" y="20" width="20" height="20" fill="#fff" />
      <rect x="60" y="20" width="20" height="20" fill="#fff" />
      <rect x="20" y="60" width="20" height="20" fill="#fff" />
      <rect x="60" y="60" width="20" height="20" fill="#fff" />
    </svg>
  );
}

function storeInviteFromUrlOnce() {
  try {
    const params = new URLSearchParams(window.location.search);
    const b = params.get("boardId");
    const t = params.get("inviteToken");
    if (b && t) {
      localStorage.setItem("pendingBoardId", b);
      localStorage.setItem("pendingInviteToken", t);
    }
  } catch {}
}

function App() {
  storeInviteFromUrlOnce();
  const [user, setUser] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlBoardId = params.get("boardId");
      return urlBoardId || localStorage.getItem("lastBoardId") || "";
    } catch {
      return "";
    }
  });
  const [authView, setAuthView] = useState("login"); // 'login' oder 'register'
  const [checkedSession, setCheckedSession] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // Topbar-Menü (Handy/Tablet)
  const topbarRef = useRef(null);

  // Topbar-Menü: Klick außerhalb + ESC schließt
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (topbarRef.current && !topbarRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Beim Board-Wechsel Menü schließen
  useEffect(() => setMenuOpen(false), [selectedBoardId]);

  // Session rehydrieren (Cookie oder Bearer wird serverseitig geprüft)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/me"); // => { id, name, email }
        if (mounted) setUser(data);
      } catch {
        if (mounted) setUser(null);
      } finally {
        if (mounted) setCheckedSession(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch {}
    clearAuthToken();
    setUser(null);
    setBoards([]);
    setSelectedBoardId("");
    try { localStorage.removeItem("lastBoardId"); } catch {}
  };

  // Boards abrufen
  const fetchBoards = useCallback(async () => {
    try {
      const res = await api.get("/boards");
      setBoards(res.data);
      const stored = (() => {
        try {
          return localStorage.getItem("lastBoardId") || "";
        } catch {
          return "";
        }
      })();

      const exists = (id) => res.data.some((b) => b.id.toString() === id);
      if (res.data.length === 0) {
        setSelectedBoardId("");
        return;
      }

      if (stored && exists(stored)) {
        setSelectedBoardId(stored);
        return;
      }

      if (selectedBoardId && exists(selectedBoardId)) {
        // keep current selection
        return;
      }

      setSelectedBoardId(res.data[0].id.toString());
    } catch (err) {
      console.error(err);
    }
  }, [selectedBoardId]);

  useEffect(() => {
    if (!user) return;
    const pendingBoardId = localStorage.getItem("pendingBoardId");
    const pendingInviteToken = localStorage.getItem("pendingInviteToken");
    if (pendingBoardId && pendingInviteToken) {
      (async () => {
        try {
          await api.post("/invites/accept", {
            boardId: Number(pendingBoardId),
            inviteToken: pendingInviteToken,
          });
        } catch (err) {
          console.error("Fehler beim Einlösen der Einladung:", err);
        } finally {
          localStorage.removeItem("pendingBoardId");
          localStorage.removeItem("pendingInviteToken");
          fetchBoards();
        }
      })();
    } else {
      fetchBoards();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBoardCreated = (newBoard) => {
    setBoards((prev) => [...prev, newBoard]);
    setSelectedBoardId(newBoard.id.toString());
  };

  const handleTaskMoved = ({ task, fromBoardId, toBoardId }) => {
    setBoards((prev) =>
      prev.map((b) => {
        if (b.id === Number(fromBoardId)) {
          return { ...b, tasks: (b.tasks || []).filter((t) => t.id !== task.id) };
        }
        if (b.id === Number(toBoardId)) {
          const remaining = Array.isArray(b.tasks) ? b.tasks.filter((t) => t.id !== task.id) : [];
          return { ...b, tasks: [...remaining, task] };
        }
        return b;
      })
    );
  };

  // Board löschen
  const handleDeleteBoard = async () => {
    if (!selectedBoardId) return;

    const confirmDelete = window.confirm(
      "Willst du dieses Board wirklich löschen? Alle Aufgaben und Daten werden entfernt."
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/boards/${selectedBoardId}`);
      const newBoards = boards.filter(
        (board) => board.id.toString() !== selectedBoardId
      );
      setBoards(newBoards);
      setSelectedBoardId(newBoards.length ? newBoards[0].id.toString() : "");
    } catch (err) {
      console.error(err);
    }
  };

  // Auswahl persistent speichern + URL aktualisieren
  useEffect(() => {
    try {
      if (selectedBoardId) localStorage.setItem("lastBoardId", selectedBoardId);
      else localStorage.removeItem("lastBoardId");
    } catch {}
    
    // URL aktualisieren (ohne Seite zu laden)
    const params = new URLSearchParams(window.location.search);
    if (selectedBoardId) {
      params.set("boardId", selectedBoardId);
    } else {
      params.delete("boardId");
    }
    const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState(null, "", newUrl);
  }, [selectedBoardId]);

  const selectedBoard = boards.find((b) => b.id.toString() === selectedBoardId);

  // Bevor wir wissen, ob eine Session existiert -> kurzer Loader
  if (!checkedSession) {
    return (
      <div className="app-loading" aria-busy="true">
        <div className="spinner" />
      </div>
    );
  }

  // Nicht angemeldet: Landingpage + Auth-Box
  if (!user) {
    return (
      <div className="app-shell">
        <header className="topbar">
          <div className="topbar-brand">
            <Logo />
            <span>GeHa-Projects</span>
          </div>
          <ThemeToggle />
        </header>

        <main className="landing">
          <LandingPage />
          <div className="auth-card">
            {authView === "login" ? (
              <>
                <Login setUser={setUser} />
                <p className="auth-switch">
                  Noch kein Konto?{" "}
                  <button
                    className="link-button"
                    onClick={() => setAuthView("register")}
                  >
                    Registriere dich
                  </button>
                </p>
              </>
            ) : (
              <>
                <Register setUser={setUser} />
                <p className="auth-switch">
                  Schon ein Konto?{" "}
                  <button
                    className="link-button"
                    onClick={() => setAuthView("login")}
                  >
                    Einloggen
                  </button>
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  const isBoardAdmin = selectedBoard && selectedBoard.admin === user.id;

  // Angemeldet: Board-Übersicht
  return (
    <div className="app-shell">
      <header className="topbar" ref={topbarRef}>
        <div className="topbar-brand">
          <Logo />
          <span className="topbar-brand-name">GeHa-Projects</span>
        </div>

        <div className="topbar-board">
          <select
            id="boardSelect"
            className="board-select"
            aria-label="Board auswählen"
            value={selectedBoardId}
            onChange={(e) => setSelectedBoardId(e.target.value)}
          >
            <option value="" disabled>
              -- Wähle ein Board --
            </option>
            {boards.map((b) => (
              <option key={b.id} value={b.id.toString()}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <button
          className="icon-btn topbar-menu-toggle"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-controls="topbar-actions"
          aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <div
          id="topbar-actions"
          className={`topbar-actions ${menuOpen ? "is-open" : ""}`}
        >
          <CollapsibleCreateBoardForm onBoardCreated={handleBoardCreated} />

          {isBoardAdmin && <InviteUserForm board={selectedBoard} />}

          {selectedBoard && (
            <>
              <span className="topbar-actions-label">Mitglieder</span>
              <InvitedUsers board={selectedBoard} currentUser={user} />
            </>
          )}

          <span className="topbar-divider" aria-hidden="true" />

          {isBoardAdmin && (
            <button className="btn btn-danger-ghost" onClick={handleDeleteBoard}>
              <FaTrash /> Board löschen
            </button>
          )}

          <button className="btn btn-ghost" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>

        <ThemeToggle />
      </header>

      <main className="app-main">
        {selectedBoard ? (
          <Board
            key={selectedBoard.id}
            board={selectedBoard}
            currentUser={user}
            allBoards={boards}
            onTaskMoved={handleTaskMoved}
          />
        ) : (
          <div className="empty-state">Kein Board ausgewählt.</div>
        )}
      </main>
    </div>
  );
}

export default App;
